import { FormEvent, useState } from "react";
import { GRSView } from "./GRSView";
import { useNavigate } from "react-router-dom";
import {
  RatingInstrumentSubmissionContext,
  RatingInstrumentSubmissionItem,
  RatingInstrumentSubmissionModule,
} from "services/database/rating_instruments/schemas/RatingInstrumentSubmission";
import {
  ENDOMETRIAL_BIOPSY,
  INTRA_UTERINE_DEVICE_INSERTION,
  ROUTINE_PESSARY_CARE,
  VULVA_PUNCH_BIOPSY,
} from "navigation/Paths";

export const GRSContainer = ({
  module,
  context,
}: {
  module: RatingInstrumentSubmissionModule;
  context: RatingInstrumentSubmissionContext;
}) => {
  const [scores, setScores] = useState<{ [id: string]: number }>({});
  const [comments, setComments] = useState<{ [id: string]: string }>({});
  const [page, setPage] = useState(0);
  const [traineeId, setTraineeId] = useState<string | null>(null);

  const navigate = useNavigate();
  console.log(`grs: page: ${page}`);

  function setComment(id: string, text: string) {
    let newComments: { [id: string]: string } = {
      ...comments,
    };
    newComments[id] = text;
    setComments({
      ...newComments,
    });
  }

  // Function to set score
  function setScore(id: string, score: number) {
    // Log the id and score
    console.log(`${id}: ${score}`);
    // pass a function that takes the previous state and returns the new state
    setScores(prevScores => {
      // We return a new object that has all the old scores, plus the new score
      return {
        ...prevScores,
        [id]: score
      }
    });
    
    return true;
  }

  function getTotalScore() {
    let totalScore = 0;
    for (var score in scores) {
      totalScore += scores[score];
    }
    return totalScore;
  }

  function getMaxScore() {
    let maxScore = 0;
    getModuleData(module).data.map((row) => (maxScore += row.steps.length));
    return maxScore;
  }

  function getSubmitData() {
    let submitData = getModuleData(module).data.reduce((arr: any[], row) => {
      arr.push({
        id: null,
        name: row.name,
        score: scores[row.id],
        comment: comments[row.id] ?? "",
      });
      return arr;
    }, []);

    return submitData;
  }

  function getDataToSubmit() {
    let items = getModuleData(module).data.reduce(
      (arr: RatingInstrumentSubmissionItem[], item) => {
        if (item.id !== null) {
          arr.push({
            id: item.id,
            name: item.name,
            score: scores[item.id],
            comment: comments[item.id] ?? "",
          });
        }
        return arr;
      },
      []
    );

    return {
      items: items,
      traineeId: traineeId,
      totalScore: getTotalScore(),
    };
  }

  function startNewTest() {
    setScores({});
    setComments({});
    setTraineeId(null);
    setPage(0);
  }

  async function submitResult() {
    setPage(2);
  }

  const goToSubmit = (e: FormEvent) => {
    let data = getDataToSubmit();
    for (const item of data.items) {
      if (isNaN(item.score)) {
        e.preventDefault();
        alert("Please select a score for each parameter prior to proceeding.");
        return;
      }
    }
    setPage(1);
  };

  const functions: { [k: string]: any } = {};
  functions.setScore = setScore;
  functions.setTraineeId = setTraineeId;
  functions.setComment = setComment;
  functions.goToSuccess = () => submitResult();
  functions.goToSubmit = goToSubmit;
  functions.goToRate = () => setPage(0);
  functions.goToNewRate = () => startNewTest();
  functions.backToGrsMenu = () => navigate("..");

  const objects: { [k: string]: any } = {};
  objects.rateData = getModuleData(module).data;
  objects.submitData = getSubmitData();
  objects.comments = comments;
  objects.scores = scores;
  objects.page = page;
  objects.currentModuleName = getModuleData(module).title;
  objects.traineeId = traineeId;
  objects.totalScore = getTotalScore();
  objects.maxScore = getMaxScore();
  objects.moduleId = module;

  return <GRSView functions={functions} objects={objects} />;
};

function createRaterData(
  id: string,
  name: string,
  one: string,
  three: string,
  five: string
) {
  return {
    id,
    name,
    steps: [one, "", three, "", five],
  };
}

const rateData = [
  createRaterData(
    "1",
    "Knowledge of instruments",
    "Frequently asked for wrong instrument or used inappropriate instrument",
    "Knows names of most instruments and uses appropriate instrument",
    "Obviously familiar with the instruments and their names"
  ),
  createRaterData(
    "2",
    "Instruments handling",
    "Repeatedly made tentative or awkward movements with instruments",
    "Competent use of instruments although occasionally appeared uncertain or awkward",
    "Fluid movement with instruments and no awkwardness"
  ),
  createRaterData(
    "3",
    "Respect for tissue",
    "Frequently used unnecessary forces on tissue or causes damage by inappropriate use of instruments",
    "Careful handling of tissue but occasionally caused inadvertent damage",
    "Consistently handled tissue appropriately with minimum to no damage"
  ),
  createRaterData(
    "4",
    "Economy of movement",
    "Many unnecessary movements",
    "Efficient motion but some unnecessary movements",
    "Maximum economy of movements"
  ),
  createRaterData(
    "5",
    "Flow of procedure/forward planning",
    "Imprecise, incorrect technique in approaching the procedure",
    "Careful technique with occasional errors",
    "Obviously planned course of procedure with fluid, safe and correct techniques in all stages of the procedure"
  ),
  createRaterData(
    "6",
    "Use of assistant(s)",
    "Consistently required the help of the assistant or failed to use the assistant when required",
    "Appropriate use of the assistant most the time",
    "Strategically used the assistant of the best advantage at all time"
  ),
];

const modulesData = {
  endometrial_biopsy: {
    title: "Endometrial Biopsy",
    data: rateData,
    id: ENDOMETRIAL_BIOPSY,
  },
  routine_pessary_care: {
    title: "Routine Pessary Care",
    data: rateData,
    id: ROUTINE_PESSARY_CARE,
  },
  intrauterine_insertion: {
    title: "Intrauterine Device/System Insertion",
    data: rateData,
    id: INTRA_UTERINE_DEVICE_INSERTION,
  },
  vulva_punch_bipsy: {
    title: "Vulva Punch Biopsy",
    data: rateData,
    id: VULVA_PUNCH_BIOPSY,
  },
  default: {
    title: "",
    data: [],
    id: "",
  },
};

const getModuleData = (moduleId: string) => {
  switch (moduleId) {
    case ENDOMETRIAL_BIOPSY:
      return modulesData.endometrial_biopsy;
    case ROUTINE_PESSARY_CARE:
      return modulesData.routine_pessary_care;
    case INTRA_UTERINE_DEVICE_INSERTION:
      return modulesData.intrauterine_insertion;
    case VULVA_PUNCH_BIOPSY:
      return modulesData.vulva_punch_bipsy;
    default:
      return modulesData.default;
  }
};
